import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, AlertTriangle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

const REASONS = [
  ["feeling_great", "Feeling great — want more"],
  ["want_harder", "Want it harder today"],
  ["want_easier", "Want it easier today"],
  ["no_time", "Short on time today"],
  ["opportunity", "An opportunity came up (group ride/run, event)"],
  ["not_feeling", "Not feeling it / tired"],
  ["pain", "Pain or niggle"],
  ["cant_train", "Can't train at all today"],
  ["other", "Something else"],
];

export default function AdjustFlow({ workoutId, open, onOpenChange, onDone }) {
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState(null);

  const reset = () => { setReason(null); setNote(""); setDecision(null); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const submit = async () => {
    setBusy(true);
    try {
      const label = REASONS.find((r) => r[0] === reason)?.[1] || reason;
      const { data } = await api.post(`/workout/${workoutId}/adjust`, { reason: label, note: note || null });
      setDecision(data.decision);
      onDone(data);
    } catch (e) {
      toast("Couldn't adjust", { description: formatApiErrorDetail(e.response?.data?.detail) || "Try again" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="adjust-flow">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold text-2xl tracking-tight">
            {decision ? "Caden's call" : "Adjust today"}
          </DialogTitle>
          <DialogDescription>{decision ? "Here's how I'd handle it." : "Tell me what changed and I'll rework today the smart way."}</DialogDescription>
        </DialogHeader>

        {!decision ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 pt-1">
            <p className="text-sm text-muted-foreground">Life happens — tell me what's going on and I'll rework today the smart way (I won't just skip it).</p>
            <div className="grid grid-cols-1 gap-2" data-testid="adjust-reasons">
              {REASONS.map(([k, label]) => (
                <button key={k} data-testid={`adjust-reason-${k}`} onClick={() => setReason(k)}
                  className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${reason === k ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card text-foreground hover:border-primary/50"}`}>
                  {label}
                </button>
              ))}
            </div>
            <Textarea data-testid="adjust-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add any detail (optional)" rows={2} />
            <Button data-testid="adjust-submit" onClick={submit} disabled={busy || !reason} className="w-full h-12 rounded-xl font-semibold active:scale-95 transition-transform">
              {busy ? "Caden is thinking…" : "Ask Caden"}
            </Button>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 pt-1">
            <div className={`rounded-xl p-4 flex items-start gap-3 ${decision.concern ? "bg-brand-orange/10 border border-brand-orange/30" : "bg-muted/60"}`} data-testid="adjust-decision">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${decision.concern ? "bg-brand-orange/20" : "brand-gradient"}`}>
                {decision.concern ? <AlertTriangle className="text-brand-orange" size={18} /> : <Sparkles className="text-white" size={16} />}
              </div>
              <div>
                {decision.concern && <p className="text-[11px] font-bold uppercase tracking-widest text-brand-orange mb-1" data-testid="adjust-concern">Heads up</p>}
                <p className="text-foreground leading-relaxed text-[15px]" data-testid="adjust-message">{decision.athlete_message}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDecision(null)} data-testid="adjust-reconsider" className="rounded-xl flex-1"><ArrowLeft size={15} className="mr-1" /> Change reason</Button>
              <Button onClick={() => close(false)} data-testid="adjust-done" className="rounded-xl flex-1 font-semibold">Got it</Button>
            </div>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}
